import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { planLabel } from "@/lib/plans";
import {
  Users, CreditCard, UserCircle2, ScrollText, ShieldCheck, Sparkles,
  Wallet, AlertTriangle, ArrowUpRight, ShoppingCart, ClipboardCheck,
} from "lucide-react";

const EURO = new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
});
const EURO_PRECISE = new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", maximumFractionDigits: 2,
});

export default function Dashboard() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ members: 0, audit: 0 });
  const [cash, setCash] = useState(null); // null = loading, {} = loaded
  const [purchasesKpi, setPurchasesKpi] = useState(null);
  const hasInvoicing = session.features?.includes("invoicing");
  const hasExpenses = session.features?.includes("expenses");

  useEffect(() => {
    (async () => {
      try {
        const [m, a] = await Promise.all([
          api.get("/tenant/members").catch(() => ({ data: { members: [] } })),
          api.get("/tenant/audit-log?limit=1000").catch(() => ({ data: { count: 0 } })),
        ]);
        setStats({ members: m.data.members?.length || 0, audit: a.data.count || 0 });
      } catch { /* ignore */ }
    })();
  }, [session?.tenant?.id]);

  useEffect(() => {
    if (!hasInvoicing) { setCash({ outstanding_ttc: 0, overdue_30d_ttc: 0, outstanding_count: 0, overdue_30d_count: 0, aging_buckets: null }); return; }
    (async () => {
      try {
        const r = await api.get("/dashboard/cash-health");
        setCash(r.data);
      } catch {
        setCash({ outstanding_ttc: 0, overdue_30d_ttc: 0, outstanding_count: 0, overdue_30d_count: 0, aging_buckets: null });
      }
    })();
  }, [session?.tenant?.id, hasInvoicing]);

  useEffect(() => {
    if (!hasExpenses) return;
    (async () => {
      try {
        const r = await api.get("/dashboard/purchases-kpi");
        setPurchasesKpi(r.data);
      } catch {
        setPurchasesKpi({ spent_month_ttc: 0, expense_reports_pending_count: 0,
                          awaiting_validation_count: 0 });
      }
    })();
  }, [session?.tenant?.id, hasExpenses]);

  const kpis = [
    { key: "members", value: stats.members, icon: Users, testid: "kpi-members" },
    { key: "plan", value: planLabel(session.tenant.plan_code), icon: CreditCard, testid: "kpi-plan" },
    { key: "role", value: session.role_label, icon: UserCircle2, testid: "kpi-role" },
    { key: "audit", value: stats.audit, icon: ScrollText, testid: "kpi-audit" },
  ];

  const activeModules = [
    { key: "members", label: t("sidebar.members"), icon: Users },
    { key: "auditLog", label: t("sidebar.auditLog"), icon: ScrollText },
    { key: "plan", label: t("sidebar.plan"), icon: CreditCard },
    { key: "settings", label: t("sidebar.settings"), icon: ShieldCheck },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          {t("dashboard.title")}
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("dashboard.welcome")}, {session.user.full_name?.split(" ")[0] || session.user.email}
        </h1>
        <p className="mt-1.5 text-sm text-slate-500">
          {session.tenant.name}
          {session.tenant.is_demo && (
            <span className="ml-2 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700"
                  data-testid="tenant-demo-badge">
              {t("tenantSwitcher.demo")}
            </span>
          )}
        </p>
      </div>

      {/* Cash health — only when invoicing feature is enabled */}
      {hasInvoicing && (
        <CashHealthBlock cash={cash} navigate={navigate} t={t} />
      )}

      {/* Purchases KPI — only when expenses feature is enabled */}
      {hasExpenses && (
        <PurchasesBlock kpi={purchasesKpi} navigate={navigate} t={t} />
      )}

      {/* KPIs (generic) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ key, value, icon: Icon, testid }) => (
          <Card key={key} className="p-5 border-slate-200 rounded-xl bg-white hover:shadow-sm transition-shadow"
                data-testid={testid}>
            <div className="flex items-start justify-between">
              <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
                {t(`dashboard.kpi.${key}`)}
              </div>
              <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            </div>
            <div className="mt-3 text-2xl font-semibold text-slate-900"
                 style={{ fontFamily: "Manrope, sans-serif" }}>
              {value}
            </div>
          </Card>
        ))}
      </div>

      {/* Active modules */}
      <div>
        <h2 className="text-lg font-medium text-slate-800">{t("dashboard.modules")}</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {activeModules.map(({ key, label, icon: Icon }) => (
            <div key={key} className="p-4 rounded-xl border border-slate-200 bg-white flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <Icon className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
              </div>
              <div className="text-sm font-medium text-slate-800">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Future modules preview */}
      <div>
        <h2 className="text-lg font-medium text-slate-800">{t("dashboard.futureModules")}</h2>
        <p className="mt-1 text-sm text-slate-500">
          Ces modules seront progressivement activés selon la feuille de route (Phases 1 à 6+).
        </p>
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-x divide-y divide-slate-200 sm:[&>*:nth-child(even)]:border-l">
            {[
              ["invoicing", "Facturation"],
              ["expenses", "Achats & Notes de frais"],
              ["treasury", "Trésorerie & Rapprochement"],
              ["accounting", "Comptabilité"],
              ["ai_copilot", "Assistant IA"],
              ["hr", "Ressources Humaines"],
            ]
              .filter(([feat]) => !session.features?.includes(feat))
              .map(([feat, phase]) => (
              <div key={feat} className="p-4 flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-slate-300" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-700">{t(`sidebar.${feat}`)}</div>
                  <div className="text-xs text-slate-400">{phase}</div>
                </div>
                <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">
                  {t("common.soon")}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function PurchasesBlock({ kpi, navigate, t }) {
  const loading = kpi === null;
  const spent = kpi?.spent_month_ttc ?? 0;
  const spentCount = kpi?.spent_month_count ?? 0;
  const pending = kpi?.expense_reports_pending_count ?? 0;
  const pendingAmount = kpi?.expense_reports_pending_amount ?? 0;
  const awaiting = kpi?.awaiting_validation_count ?? 0;
  const monthLabel = new Date().toLocaleDateString("fr-FR", { month: "long" });

  return (
    <section data-testid="purchases-kpi-section">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-medium text-slate-800">Achats & Dépenses</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Dépenses du mois en cours et notes de frais en attente de validation.
          </p>
        </div>
        <button type="button" onClick={() => navigate("/app/purchases")}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                data-testid="purchases-kpi-view-link">
          Voir les achats
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Spent this month */}
        <Card className="p-5 border-slate-200 rounded-xl bg-white hover:shadow-sm transition-shadow"
              data-testid="kpi-spent-month">
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              Dépenses ({monthLabel})
            </div>
            <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <ShoppingCart className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
            </div>
          </div>
          <div className="mt-3 text-3xl font-semibold text-slate-900"
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="kpi-spent-month-value">
            {loading ? "—" : new Intl.NumberFormat("fr-FR", {
              style: "currency", currency: "EUR", maximumFractionDigits: 0,
            }).format(spent)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {loading ? " " : (spentCount === 0 ? "Aucune dépense validée ce mois" :
              (spentCount === 1 ? "1 opération validée" : `${spentCount} opérations validées`))}
          </div>
        </Card>

        {/* Awaiting validation */}
        <Card className={`p-5 border rounded-xl bg-white hover:shadow-sm transition-shadow ${
                awaiting > 0 ? "border-amber-200" : "border-slate-200"}`}
              data-testid="kpi-awaiting-validation">
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              À valider
            </div>
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
              awaiting > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
              <AlertTriangle className={`h-4 w-4 ${awaiting > 0 ? "text-amber-600" : "text-slate-400"}`}
                             strokeWidth={1.75} />
            </div>
          </div>
          <div className={`mt-3 text-3xl font-semibold ${awaiting > 0 ? "text-amber-800" : "text-slate-900"}`}
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="kpi-awaiting-validation-value">
            {loading ? "—" : awaiting}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {loading ? " " : "factures / dépenses en attente"}
          </div>
        </Card>

        {/* Expense reports pending */}
        <Card className={`p-5 border rounded-xl bg-white hover:shadow-sm transition-shadow ${
                pending > 0 ? "border-indigo-200" : "border-slate-200"}`}
              data-testid="kpi-reports-pending">
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              Notes de frais en attente
            </div>
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
              pending > 0 ? "bg-indigo-50" : "bg-slate-50"}`}>
              <ClipboardCheck className={`h-4 w-4 ${pending > 0 ? "text-indigo-600" : "text-slate-400"}`}
                              strokeWidth={1.75} />
            </div>
          </div>
          <div className={`mt-3 text-3xl font-semibold ${pending > 0 ? "text-indigo-800" : "text-slate-900"}`}
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="kpi-reports-pending-value">
            {loading ? "—" : pending}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {loading ? " " : (pending === 0 ? "aucune en attente" :
              `${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR",
                                                  maximumFractionDigits: 0 }).format(pendingAmount)} à rembourser`)}
          </div>
        </Card>
      </div>
    </section>
  );
}


function CashHealthBlock({ cash, navigate, t }) {
  const loading = cash === null;
  const outstanding = cash?.outstanding_ttc ?? 0;
  const overdue = cash?.overdue_30d_ttc ?? 0;
  const outstandingCount = cash?.outstanding_count ?? 0;
  const overdueCount = cash?.overdue_30d_count ?? 0;
  const overdueRatio = outstanding > 0 ? overdue / outstanding : 0;
  const buckets = cash?.aging_buckets || null;

  return (
    <section data-testid="cash-health-section">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-medium text-slate-800">{t("dashboard.cashHealth.title")}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{t("dashboard.cashHealth.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/invoices")}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
          data-testid="cash-health-view-invoices"
        >
          {t("dashboard.cashHealth.viewInvoices")}
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Outstanding total */}
        <Card className="p-5 border-slate-200 rounded-xl bg-white hover:shadow-sm transition-shadow"
              data-testid="kpi-outstanding">
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              {t("dashboard.kpi.outstanding")}
            </div>
            <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Wallet className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
            </div>
          </div>
          <div className="mt-3 text-3xl font-semibold text-slate-900"
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="kpi-outstanding-value">
            {loading ? "—" : EURO.format(outstanding)}
          </div>
          <div className="mt-1 text-xs text-slate-500" data-testid="kpi-outstanding-count">
            {loading ? " " : t("dashboard.cashHealth.countSuffix", { count: outstandingCount })}
          </div>
        </Card>

        {/* Overdue > 30d */}
        <Card
          className={`p-5 border rounded-xl bg-white hover:shadow-sm transition-shadow ${
            overdue > 0 ? "border-red-200" : "border-slate-200"
          }`}
          data-testid="kpi-overdue30"
        >
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              {t("dashboard.kpi.overdue30")}
            </div>
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
              overdue > 0 ? "bg-red-50" : "bg-slate-50"
            }`}>
              <AlertTriangle className={`h-4 w-4 ${overdue > 0 ? "text-red-600" : "text-slate-400"}`}
                             strokeWidth={1.75} />
            </div>
          </div>
          <div className={`mt-3 text-3xl font-semibold ${overdue > 0 ? "text-red-700" : "text-slate-900"}`}
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="kpi-overdue30-value">
            {loading ? "—" : EURO.format(overdue)}
          </div>
          <div className="mt-1 text-xs text-slate-500 flex items-center gap-2" data-testid="kpi-overdue30-count">
            <span>{loading ? " " : t("dashboard.cashHealth.countSuffix", { count: overdueCount })}</span>
            {!loading && overdue > 0 && (
              <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                {(overdueRatio * 100).toFixed(0)} % du reste dû
              </span>
            )}
          </div>
        </Card>
      </div>

      {/* Aging breakdown */}
      {buckets && outstanding > 0 && (
        <Card className="mt-4 p-5 border-slate-200 rounded-xl bg-white" data-testid="aging-breakdown">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-3">
            {t("dashboard.cashHealth.aging.title")}
          </div>
          <AgingBar buckets={buckets} outstanding={outstanding} t={t} />
        </Card>
      )}

      {!loading && outstanding === 0 && (
        <Card className="mt-4 p-5 border-slate-200 rounded-xl bg-emerald-50/40 text-sm text-emerald-800"
              data-testid="cash-health-empty">
          {t("dashboard.cashHealth.empty")}
        </Card>
      )}
    </section>
  );
}


function AgingBar({ buckets, outstanding, t }) {
  const order = ["current", "0_30", "31_60", "61_90", "over_90"];
  const colors = {
    current:  { bar: "bg-slate-400",   dot: "bg-slate-400" },
    "0_30":   { bar: "bg-amber-400",  dot: "bg-amber-400" },
    "31_60":  { bar: "bg-orange-500", dot: "bg-orange-500" },
    "61_90":  { bar: "bg-red-500",    dot: "bg-red-500" },
    over_90:  { bar: "bg-red-700",    dot: "bg-red-700" },
  };
  return (
    <div>
      <div className="h-2.5 w-full rounded-full overflow-hidden bg-slate-100 flex">
        {order.map((k) => {
          const amt = buckets[k]?.amount || 0;
          if (amt <= 0) return null;
          const pct = (amt / outstanding) * 100;
          return (
            <div
              key={k}
              className={`h-full ${colors[k].bar}`}
              style={{ width: `${pct}%` }}
              title={`${t(`dashboard.cashHealth.aging.${k}`)} — ${EURO_PRECISE.format(amt)}`}
              data-testid={`aging-bar-${k}`}
            />
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {order.map((k) => {
          const b = buckets[k] || { amount: 0, count: 0 };
          return (
            <div key={k} className="flex items-start gap-2 py-1"
                 data-testid={`aging-legend-${k}`}>
              <span className={`mt-1 inline-block h-2.5 w-2.5 rounded-full ${colors[k].dot}`} />
              <div>
                <div className="text-[11px] font-medium text-slate-700">
                  {t(`dashboard.cashHealth.aging.${k}`)}
                </div>
                <div className="text-xs text-slate-500">
                  {EURO.format(b.amount)}
                  {b.count > 0 && <span className="text-slate-400"> · {b.count}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
